import { CameraIcon, PencilSquareIcon, StarIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * VH-165 — the one place to log a meal on a phone.
 *
 * The three ways to log used to sit in three places: "Log with Photo" and
 * "Manual Food Entry" in a desktop header row beside a text-3xl title, and
 * Favourites in a `lg:col-span-1` sidebar that stacks to the BOTTOM of the page
 * on a phone — below the whole day's meals and Recent Days. Two actions at the
 * top, one far below, none of them grouped.
 *
 * Photo is the primary action because it is the most common one and because
 * workout's CaptureFlow (VH-126) set the pattern: one full-width button that
 * opens the camera on tap.
 *
 * PRESENTATIONAL ONLY. It owns no logging state and calls no API — every
 * handler comes from Nutrition.jsx, which stays the single owner. The mobile
 * block and the desktop header are two presentations of ONE state; two copies
 * would reproduce the empty-modal fault VH-162 had to fix.
 */
export default function MealLogActions({
  onTakePhoto,
  onChooseFromLibrary,
  onManualEntry,
  onFavourites,
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3" data-testid="meal-log-actions">
      <button
        type="button"
        onClick={onTakePhoto}
        className="btn-primary w-full flex items-center justify-center gap-2 py-4 text-lg"
        data-testid="meal-log-take-photo"
      >
        <CameraIcon className="h-6 w-6" />
        {t('nutrition.actions.takePhoto', 'Take a photo')}
      </button>

      <button
        type="button"
        onClick={onChooseFromLibrary}
        className="w-full text-center text-sm text-gray-500 hover:text-gray-900 underline"
        data-testid="meal-log-choose-library"
      >
        {t('nutrition.actions.chooseFromLibrary', 'Choose from library')}
      </button>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onManualEntry}
          className="btn-outline flex items-center justify-center gap-2 py-3"
          data-testid="meal-log-manual"
        >
          <PencilSquareIcon className="h-5 w-5" />
          {t('nutrition.manualEntry.title')}
        </button>
        <button
          type="button"
          onClick={onFavourites}
          className="btn-outline flex items-center justify-center gap-2 py-3"
          data-testid="meal-log-favourites"
        >
          <StarIcon className="h-5 w-5" />
          {t('favorites.title', 'Favorites')}
        </button>
      </div>
    </div>
  )
}
